import React, { useState } from "react";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fieldClass =
    "w-full bg-transparent border border-white/15 px-4 py-3 text-white placeholder:text-zinc-600 focus:border-[#FF2800] focus:outline-none transition-colors af-mono text-sm";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Header />

      <section className="border-b border-white/10 bg-black/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-12 md:pt-28">
          <p className="af-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
            // Hit the radio
          </p>
          <h1
            className="af-display text-5xl md:text-7xl font-black uppercase tracking-tighter leading-[0.92]"
            data-testid="contact-title"
          >
            Talk to the pit
            <br />
            <span className="af-outline-text">crew.</span>
          </h1>
          <p className="text-zinc-400 mt-6 max-w-2xl">
            Questions about a frame, a custom build, a launch drop or shipping
            timelines? Pick up the phone or send a note.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5 space-y-6">
          <a
            href="tel:+919820078697"
            className="af-card p-8 block hover:border-[#FF2800] transition-colors"
            data-testid="contact-phone-card"
          >
            <Phone size={20} strokeWidth={1.5} className="text-[#FF2800]" />
            <p className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-4">
              // Call us
            </p>
            <p className="af-mono text-2xl md:text-3xl font-semibold mt-2 tracking-tight">
              +91 98200 78697
            </p>
            <p className="text-zinc-400 text-sm mt-3">
              Mon–Sat, 10:00 — 19:00 IST
            </p>
          </a>

          <div className="af-card p-8">
            <Mail size={20} strokeWidth={1.5} className="text-[#00E5FF]" />
            <p className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-4">
              // Email
            </p>
            <p className="af-mono text-lg mt-2">contact@apexdzns.com</p>
          </div>

          <div className="af-card p-8">
            <MapPin size={20} strokeWidth={1.5} className="text-zinc-300" />
            <p className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-4">
              // Studio
            </p>
            <p className="text-zinc-300 mt-2">Mumbai · India</p>
            <p className="text-zinc-500 text-sm mt-1">Visits by appointment.</p>
          </div>
        </div>

        <div className="md:col-span-7">
          <form
            onSubmit={submit}
            className="af-card p-8 md:p-10 space-y-6"
            data-testid="contact-form"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <label className="block">
                <span className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                  Name *
                </span>
                <input
                  required
                  type="text"
                  className={fieldClass}
                  value={form.name}
                  onChange={handle("name")}
                  data-testid="contact-name-input"
                />
              </label>
              <label className="block">
                <span className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                  Email *
                </span>
                <input
                  required
                  type="email"
                  className={fieldClass}
                  value={form.email}
                  onChange={handle("email")}
                  data-testid="contact-email-input"
                />
              </label>
            </div>
            <label className="block">
              <span className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                Message *
              </span>
              <textarea
                required
                rows={6}
                className={`${fieldClass} resize-none`}
                value={form.message}
                onChange={handle("message")}
                data-testid="contact-message-input"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#FF2800] text-white font-semibold uppercase tracking-wider py-4 hover:bg-[#CC2000] disabled:opacity-50 transition-colors"
              data-testid="contact-submit-button"
            >
              {loading ? "Sending…" : "Send message"} <Send size={14} />
            </button>
          </form>
        </div>
      </section>
      <Footer />
    </div>
  );
}
